// GradeHurdleCourseDetails.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";

/** ----------------------------------------------------------------
 * GradeHurdleCourseDetails
 * -----------------------------------------------------------------
 * Props:
 *   - platformUrl: e.g., https://platform-extensions-int.blackboard.com
 *   - backEndUrl:  e.g., https://your-backend.example.com
 *   - locale:      optional i18n key (default "en_US")
 *
 * URL params expected:
 *   - ?token=...      OAuth bearer for Learn calls
 *   - ?courseId=...   course pk1 (required for write)
 *   - ?uuid=...       current user's Learn UUID
 * ----------------------------------------------------------------*/

/** ---------------- i18n ---------------- */
const MESSAGES = {
    en_US: {
        ui: {
            loading: "Loading grade hurdle details…",
            accessRestrictedTitle: "Access Restricted",
            accessRestrictedBody: "The Grade Hurdle tool is not available to your role.",
            capabilityOffTitle: "Grade Hurdle Not Available",
            capabilityOffBody: "This capability is not enabled for this course.",
            errorTitle: "Error",
            studentCardTitle: "Grade Hurdle Policy",
            noRulesStudent: "There are currently no Grade Hurdle rules defined for this course.",
            notEnabledStudent: "This course does not use Grade Hurdle rules.",
            studentCardIntro_all:
                "This course has a Grade Hurdle rule. Your overall grade may be adjusted if all of the following conditions are met:",
            studentCardIntro_any:
                "This course has a Grade Hurdle rule. Your overall grade may be adjusted if any of the following conditions are met:",
            studentCardFootnote:
                "Exact adjustments follow the course’s grading policy and may involve setting a specific grade code when a hurdle is triggered. If you have questions, please contact your instructor.",
            previewTitle: "Student-Facing Message (Preview)",
            showPreview: "Show student-facing summary",
            hidePreview: "Hide student-facing summary",
            ruleBuilderTitle: "1) Build Sub-Rules",
            saveRules: "Save Rules to Association",
            savingRules: "Saving…",
            mappingTitle: "2) Mapping (Current Schema → Adjusted Schema)",
            mappingExample: "Example: 7–4 → 3; 3 → 2; 2 → 1; 1 → 1.",
            addMappingRow: "+ Add mapping row",
            remove: "Remove",
            evaluateTitle: "3) Evaluate & Review",
            evaluate: "Evaluate Rules",
            evaluating: "Evaluating…",
            apply: "Apply Adjusted Grades → Gradebook",
            applying: "Writing…",
            noEvalRows: "No results yet. Click “Evaluate Rules”.",
            studentCol: "Student",
            currentSchemaCol: "Current Schema (final grade)",
            triggersCol: "Hurdle Triggers?",
            adjustedCol: "Adjusted Schema",
            actionsCol: "Actions",
            yes: "Yes",
            no: "No",
            overriddenSuffix: " (overridden)",
            clearOverride: "Clear Override",
            clearing: "Clearing…",
            sortByStudent: "Sort by student",
            sortByCurrent: "Sort by current schema",
            sortByTrigger: "Sort by hurdle triggers",
            sortByAdjusted: "Sort by adjusted schema",
            selectContent: "Select content item…",
            ruleLabel: (i) => `Rule ${i}`,
            ruleType_notSubmitted: "If not submitted",
            ruleType_belowPct: "If score < %",
            thresholdPlaceholder: "Threshold %",
            combine: "Combine sub-rules with:",
            and: "AND",
            or: "OR",
            addedSubRule: "+ Add sub-rule",
        },
        messages: {
            evalDone: (n) => `Evaluation complete. Reviewed ${n} student${n === 1 ? "" : "s"}.`,
            evalFailed: "Evaluation failed.",
            writeDone: (posted, o, na, nn, nc) =>
                `Write-back complete. Posted ${posted}.` +
                (o ? ` Skipped ${o} overridden.` : "") +
                (na ? ` Skipped ${na} without adjusted schema.` : "") +
                (nn ? ` Skipped ${nn} without matching notation.` : "") +
                (nc ? ` Skipped ${nc} without finalGrade column.` : ""),
            writeFailed: "Write-back failed.",
            clearedOne: (name) => `Override cleared for ${name}.`,
            clearFailed: (name, e) => `Failed to clear override for ${name}: ${e}`,
            rulesSaved: "Rules saved to association.",
            rulesFailed: "Failed to save rules.",
            missingBackEnd: "Missing backEndUrl prop.",
            missingCourse: "Missing courseId.",
            missingAccess: "Missing access token or course ID.",
            missingUuid: "Missing user UUID.",
        },
        studentFriendly: {
            notSubmitted: (name) => `“${name}” is not submitted`,
            scoreBelow: (name, pct) => `score on “${name}” is below ${pct}%`,
            conditionOn: (name) => `condition on “${name}”`,
        },
    },
};
const getM = (locale) => ({ ...MESSAGES.en_US, ...(MESSAGES[locale] || {}) });

/** ---------------- Local storage keys + helpers ---------------- */
const STORAGE_KEYS = {
    mapping: "ghcd-mapping",
    rules: "ghcd-rules",
    logic: "ghcd-logic",
    openRules: "ghcd-open-rules",
    openMapping: "ghcd-open-mapping",
    openEval: "ghcd-open-eval",
    showStudentPreview: "ghcd-show-student-preview",
};
const readBool = (k, def = true) => {
    try {
        const raw = localStorage.getItem(k);
        if (raw == null) return def;
        if (raw === "true" || raw === "1") return true;
        if (raw === "false" || raw === "0") return false;
        return !!JSON.parse(raw);
    } catch {
        return def;
    }
};
const writeBool = (k, v) => {
    try {
        localStorage.setItem(k, v ? "true" : "false");
    } catch {}
};

/** ---------------- Defaults + small utils ---------------- */
const DEFAULT_MAPPING = [
    { fromMin: 4, fromMax: 7, to: 3 },
    { fromMin: 3, fromMax: 3, to: 2 },
    { fromMin: 2, fromMax: 2, to: 1 },
    { fromMin: 1, fromMax: 1, to: 1 },
];
const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));
const sortIcon = (active, dir) => (!active ? "↕" : dir === "asc" ? "▲" : "▼");
const parseNumber = (v, fb = null) => (Number.isFinite(Number(v)) ? Number(v) : fb);
const extractSchemaFromText = (text) => {
    if (typeof text !== "string") return null;
    const m = text.match(/(^|\D)([1-7])(\D|$)/);
    return m ? Number(m[2]) : null;
};
const applyMapping = (schemaValue, mappingRules) => {
    if (schemaValue == null) return null;
    for (const m of mappingRules) {
        if (schemaValue >= m.fromMin && schemaValue <= m.fromMax) return m.to;
    }
    return schemaValue;
};

/** ---------------- Rule model ---------------- */
const RULE_TYPES = {
    NOT_SUBMITTED: "NOT_SUBMITTED",
    SCORE_BELOW_PERCENT: "SCORE_BELOW_PERCENT",
};
const makeEmptySubRule = () => ({
    id: (crypto?.randomUUID && crypto.randomUUID()) || String(Math.random()),
    type: RULE_TYPES.NOT_SUBMITTED,
    contentId: "",
    thresholdPercent: 50,
});
const sanitizeSubRule = (r) => {
    const id = r?.id || (crypto?.randomUUID && crypto.randomUUID()) || String(Math.random());
    const type = Object.values(RULE_TYPES).includes(r?.type) ? r.type : RULE_TYPES.NOT_SUBMITTED;
    const contentId = String(r?.contentId || "");
    const thresholdPercent = clamp(parseNumber(r?.thresholdPercent, 50), 0, 100);
    return { id, type, contentId, thresholdPercent };
};

/** ---------------- Backend API ---------------- */
const normalizeStatus = (s) => {
    const v = String(s || "").trim();
    if (/^active$/i.test(v)) return "Active";
    if (/^inactive$/i.test(v)) return "Inactive";
    return v || "Inactive";
};
function makeBackendApi(backEndUrl) {
    const base = (backEndUrl || "").replace(/\/+$/, "");
    const http = axios.create({ baseURL: base, timeout: 20000 });

    return {
        getAssociationForCourse: async (coursePk1) => {
            try {
                const { data } = await http.get(`/grade-hurdle-associations`, { params: { courseId: coursePk1 } });
                const arr = Array.isArray(data?.associations)
                    ? data.associations
                    : Array.isArray(data?.items)
                        ? data.items
                        : Array.isArray(data?.results)
                            ? data.results
                            : [];
                const raw =
                    arr.find(
                        (a) =>
                            (a.CoursePk1 || a.coursePk1 || a.CoursePk || a.coursePk || a.courseId) === coursePk1
                    ) || arr[0] || null;
                if (!raw) return null;

                return {
                    coursePk1: raw.CoursePk1 || raw.coursePk1 || raw.CoursePk || raw.coursePk || raw.courseId,
                    status: normalizeStatus(raw.Status || raw.status),
                    subRules: Array.isArray(raw.SubRules || raw.subRules) ? raw.SubRules || raw.subRules : [],
                    logic: raw.Logic || raw.logic || null,
                    updatedAt: raw.UpdatedAt || raw.updatedAt,
                    performanceScoreId: raw.PerformanceScoreId || raw.performanceScoreId || null,
                    performanceScoreIds: raw.PerformanceScoreIds || raw.performanceScoreIds || {},
                };
            } catch (err) {
                const status = err?.response?.status;
                if (status === 404) {
                    const e = new Error("Association not allowed (404)");
                    e.is404 = true;
                    throw e;
                }
                throw err;
            }
        },

        saveRulesToAssociation: async (coursePk1, subRules, logic) => {
            const payload = {
                CoursePk1: coursePk1,
                SubRules: (subRules || []).map(sanitizeSubRule),
                Logic: logic === "OR" ? "OR" : "AND",
            };
            const { data } = await http.patch(`/grade-hurdle-associations`, payload);
            return data;
        },

        savePerformanceScoreId: async (coursePk1, performanceScoreId) => {
            const payload = { CoursePk1: coursePk1, PerformanceScoreId: performanceScoreId };
            const { data } = await http.patch(`/grade-hurdle-associations`, payload);
            return data;
        },

        savePerformanceScoreIds: async (coursePk1, map) => {
            const payload = { CoursePk1: coursePk1, PerformanceScoreIds: map };
            const { data } = await http.patch(`/grade-hurdle-associations`, payload);
            return data;
        },
    };
}

/** ---------------- Learn API ---------------- */
function makeLearnApi(platformUrl, token, courseIdRaw) {
    const base = (platformUrl || "").replace(/\/+$/, "");
    const http = axios.create({
        baseURL: base,
        timeout: 25000,
        headers: { Authorization: `Bearer ${token}`, Accept: "application/json" },
    });

    const courseId = courseIdRaw || "";
    const cV1Public = `/learn/api/public/v1/courses/${courseId}`;
    const cV2Public = `/learn/api/public/v2/courses/${courseId}`;
    const cV2ByCourseId = `/learn/api/public/v2/courses/${encodeURIComponent(courseId)}`;

    async function pagedGet(path, params = {}, pageSize = 200) {
        const out = [];
        let offset = 0;
        while (true) {
            const { data } = await http.get(path, { params: { limit: pageSize, offset, ...params } });
            const results = data?.results ?? [];
            out.push(...results);
            if (!data?.paging?.nextPage || results.length === 0) break;
            offset += pageSize;
        }
        return out;
    }

    function pickFinalItem(container) {
        const list = Array.isArray(container?.results) ? container.results : container ? [container] : [];
        let item =
            list.find(
                (r) =>
                    r?.displayGrade &&
                    String(r.displayGrade.scaleType).toLowerCase() === "tabular" &&
                    extractSchemaFromText(r.displayGrade.text) != null
            ) ||
            list.find((r) => r?.displayGrade && extractSchemaFromText(r.displayGrade.text) != null) ||
            list.find((r) => r?.gradeNotationId) ||
            null;
        return item || null;
    }

    async function resolveSchemaFromNotationId(gradeNotationId) {
        if (!gradeNotationId) return null;
        try {
            const { data } = await http.get(`${cV1Public}/gradebook/gradeNotations`);
            const items = data?.results || [];
            const n = items.find((x) => x.id === gradeNotationId);
            const code = n?.code || ""; // "3xx"
            const m = code.match(/^([1-7])xx$/i);
            return m ? Number(m[1]) : null;
        } catch {
            return null;
        }
    }

    return {
        getCourseMembership: async (userUuid) => {
            const { data } = await http.get(`${cV1Public}/users/uuid:${userUuid}`);
            return data;
        },

        listContents: async (parentId = "ROOT") => {
            const { data } = await http.get(`${cV1Public}/contents`, { params: { parentId } });
            return data?.results || [];
        },

        listStudentEnrollments: async (limit = 200) => pagedGet(`${cV1Public}/users`, { role: "Student" }, limit),

        getUser: async (userId) => {
            const { data } = await http.get(`/learn/api/public/v1/users/${userId}`);
            return data;
        },

        listGradebookColumns: async (limit = 200) => pagedGet(`${cV2Public}/gradebook/columns`, {}, limit),

        getFinalGradeInfoByUserName: async (userName) => {
            try {
                const path = `${cV2ByCourseId}/gradebook/users/userName:${encodeURIComponent(userName)}`;
                const { data } = await http.get(path, { params: { columnId: "finalGrade" } });
                const item = pickFinalItem(data);
                if (!item) return { schema: null, columnId: null, overridden: false };

                let schema = extractSchemaFromText(item?.displayGrade?.text);
                if (schema == null && item?.gradeNotationId) schema = await resolveSchemaFromNotationId(item.gradeNotationId);

                const columnId = item?.columnId ?? item?.column?.id ?? null;
                const overridden = Boolean(item?.overridden);
                return { schema, columnId, overridden };
            } catch {
                return { schema: null, columnId: null, overridden: false };
            }
        },

        getFinalGradeInfoByUserId: async (userId) => {
            try {
                const { data } = await http.get(`${cV2Public}/gradebook/columns/finalGrade/users/${userId}`);
                const item = pickFinalItem(data);
                if (!item) return { schema: null, columnId: null, overridden: false };

                let schema = extractSchemaFromText(item?.displayGrade?.text);
                if (schema == null && item?.gradeNotationId) schema = await resolveSchemaFromNotationId(item.gradeNotationId);

                const columnId = item?.columnId ?? item?.column?.id ?? null;
                const overridden = Boolean(item?.overridden);
                return { schema, columnId, overridden };
            } catch {
                return { schema: null, columnId: null, overridden: false };
            }
        },

        listAttemptsForColumn: async (columnId, userId) => {
            const { data } = await http.get(`${cV1Public}/gradebook/columns/${columnId}/attempts`, {
                params: userId ? { userId } : {},
            });
            return data?.results || [];
        },

        listGradeNotations: async () => {
            const { data } = await http.get(`${cV1Public}/gradebook/gradeNotations`);
            return data?.results || [];
        },

        writePerfCodeToSpecificColumn: async (columnPk1, userPk1, perfCodeId, clearCommentsIfRevertOverrides = false) => {
            const path = `/learn/api/v1/courses/${courseId}/gradebook/columns/${columnPk1}/grades`;
            const params = { clearCommentsIfRevertOverrides, userId: userPk1 };
            const payload = { perfCodeId };
            const { data } = await http.post(path, payload, { params });
            return data;
        },

        clearByGradeId: async (columnPk1, gradeId, { clearCommentsIfRevertOverrides = false, includeUnpostedGrade = true } = {}) => {
            const path = `/learn/api/v1/courses/${courseId}/gradebook/columns/${columnPk1}/grades/${gradeId}`;
            const params = { clearCommentsIfRevertOverrides, expand: "lastFeedbackAuthor", includeUnpostedGrade };
            const payload = { manualGrade: "", userId: null };
            const { data } = await http.post(path, payload, { params });
            return data;
        },

        clearOverrideOnSpecificColumn: async (columnPk1, userPk1) => {
            const path = `/learn/api/v1/courses/${courseId}/gradebook/columns/${columnPk1}/grades`;
            const params = { userId: userPk1 };
            const payload = { text: null };
            const { data } = await http.post(path, payload, { params });
            return data;
        },

        listGradesForColumnAndUser: async (columnPk1, userPk1) => {
            const path = `/learn/api/v1/courses/${courseId}/gradebook/columns/${columnPk1}/grades`;
            const { data } = await http.get(path, { params: { userId: userPk1 } });
            const arr = Array.isArray(data?.results) ? data.results : data ? [data] : [];
            return arr;
        },

        getColumnUserGradeV2: async (columnPk1, userPk1) => {
            const path = `${cV2Public}/gradebook/columns/${columnPk1}/users/${userPk1}`;
            const { data } = await http.get(path);
            return data;
        },
    };
}

/** ---------------- Component ---------------- */
const GradeHurdleCourseDetails = ({ platformUrl, backEndUrl, locale = "en_US" }) => {
    const M = useMemo(() => getM(locale), [locale]);

    const location = useLocation();
    const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

    const token = queryParams.get("token");
    const courseId = localStorage.getItem("courseId") || queryParams.get("courseId") || "";
    const userUuid = queryParams.get("uuid") || "";

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [roleDenied, setRoleDenied] = useState(false);
    const [roleId, setRoleId] = useState("");
    const [isStudent, setIsStudent] = useState(false);

    const [contents, setContents] = useState([]);
    const [students, setStudents] = useState([]);
    const [columns, setColumns] = useState([]);

    const [mapping, setMapping] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem(STORAGE_KEYS.mapping)) || DEFAULT_MAPPING;
        } catch {
            return DEFAULT_MAPPING;
        }
    });
    const [subRules, setSubRules] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem(STORAGE_KEYS.rules)) || [makeEmptySubRule()];
        } catch {
            return [makeEmptySubRule()];
        }
    });
    const [logic, setLogic] = useState(() => localStorage.getItem(STORAGE_KEYS.logic) || "AND");

    const [openRules, setOpenRules] = useState(() => readBool(STORAGE_KEYS.openRules, true));
    const [openMapping, setOpenMapping] = useState(() => readBool(STORAGE_KEYS.openMapping, true));
    const [openEval, setOpenEval] = useState(() => readBool(STORAGE_KEYS.openEval, true));

    const [showStudentPreview, setShowStudentPreview] = useState(() => readBool(STORAGE_KEYS.showStudentPreview, false));

    const [inlineError, setInlineError] = useState("");
    const [inlineSuccess, setInlineSuccess] = useState("");
    const [rulesMsgError, setRulesMsgError] = useState("");
    const [rulesMsgSuccess, setRulesMsgSuccess] = useState("");

    const [clearing, setClearing] = useState({});
    const [savingAssoc, setSavingAssoc] = useState(false);

    const [isAllowed, setIsAllowed] = useState(true);

    const [performanceScoreId, setPerformanceScoreId] = useState(null);
    const associationPerfIdRef = useRef(null);

    const [performanceScoreIds, setPerformanceScoreIds] = useState({});
    const perfIdsRef = useRef({});

    const api = useMemo(() => makeLearnApi(platformUrl, token, courseId), [platformUrl, token, courseId]);
    const backend = useMemo(() => makeBackendApi(backEndUrl), [backEndUrl]);

    const compactCss = `
  .bb-container { font-size: 13px; }
  .bb-card h2 { font-size: 16px; }
  .bb-table { font-size: 12px; border-collapse: collapse; width: 100%; }
  .bb-table th, .bb-table td { padding: 6px 8px; border-bottom: 1px solid #eee; vertical-align: middle; }
  .bb-table th { font-weight: 600; text-align: left; }
  .bb-button { -webkit-appearance: none; appearance: none; background: #f6f6f6; border: 1px solid #d3d3d3; color: #222; border-radius: 6px; padding: 6px 10px; font: inherit; line-height: 1.2; cursor: pointer; }
  .bb-button:hover { background: #efefef; }
  .bb-button.danger { background: #fff1f1; border-color: #e6b3b3; color: #7a0a0a; }
  .bb-button.danger:hover { background: #fee; }
  select, input { font-size: 12px; padding: 4px 6px; height: 30px; }
`;

    const Section = ({ title, open, onToggle, children, right }) => (
        <div className="bb-card" style={{ padding: 10, border: "1px solid #ddd", borderRadius: 8 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6 }}>
                <h2 style={{ margin: 0 }}>{title}</h2>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    {right}
                    <button type="button" className="bb-button" onClick={onToggle} aria-expanded={open}>
                        {open ? "Hide" : "Show"}
                    </button>
                </div>
            </div>
            {open && <div style={{ marginTop: 8 }}>{children}</div>}
        </div>
    );

    /** -------- bootstrap -------- */
    useEffect(() => {
        (async () => {
            try {
                if (!token || !courseId) throw new Error(M.messages.missingAccess);
                if (!userUuid) throw new Error(M.messages.missingUuid);

                // membership
                let membership;
                try {
                    membership = await api.getCourseMembership(userUuid);
                } catch {
                    setRoleDenied(true);
                    setLoading(false);
                    return;
                }

                const role = membership?.courseRoleId || "";
                setRoleId(role);

                // Student view
                if (role === "Student") {
                    setIsStudent(true);

                    const all = [];
                    const seen = new Set();
                    async function walk(parentId = "ROOT") {
                        const page = await api.listContents(parentId);
                        for (const item of page) {
                            if (seen.has(item.id)) continue;
                            seen.add(item.id);
                            all.push({ id: item.id, title: item.title || item.name || "(untitled)", hasChildren: !!item.hasChildren });
                            if (item.hasChildren) await walk(item.id);
                        }
                    }
                    await walk();
                    setContents(all);

                    try {
                        const assoc = await backend.getAssociationForCourse(courseId);
                        setIsAllowed(true);
                        if (assoc) {
                            const cleaned = Array.isArray(assoc.subRules) ? assoc.subRules.map(sanitizeSubRule) : [];
                            setSubRules(cleaned);
                            setLogic(assoc.logic === "OR" ? "OR" : "AND");
                        }
                    } catch (e) {
                        if (e?.is404) setIsAllowed(false);
                        else setIsAllowed(true);
                    }

                    setLoading(false);
                    return;
                }

                // Only instructors proceed
                if (role !== "Instructor") {
                    setRoleDenied(true);
                    setLoading(false);
                    return;
                }

                // Contents
                const all = [];
                const seen = new Set();
                async function walk(parentId = "ROOT") {
                    const page = await api.listContents(parentId);
                    for (const item of page) {
                        if (seen.has(item.id)) continue;
                        seen.add(item.id);
                        all.push({
                            id: item.id,
                            title: item.title || item.name || "(untitled)",
                            hasChildren: !!item.hasChildren,
                            contentHandlerId: item.contentHandler?.id || "unknown",
                            gradeColumnId: item.gradeColumnId || item.gradebookColumnId || null,
                        });
                        if (item.hasChildren) await walk(item.id);
                    }
                }
                await walk();

                // Students
                const enrolls = await api.listStudentEnrollments();
                const uniqueUserIds = [...new Set(enrolls.map((e) => e.userId).filter(Boolean))];
                const studentObjs = await Promise.all(
                    uniqueUserIds.map(async (uid) => {
                        try {
                            const u = await api.getUser(uid);
                            const name =
                                [u?.name?.given || u?.givenName, u?.name?.family || u?.familyName].filter(Boolean).join(" ") ||
                                u?.userName ||
                                uid;
                            return { userId: uid, userName: u?.userName, name };
                        } catch {
                            return { userId: uid, userName: null, name: uid };
                        }
                    })
                );

                // Columns
                const cols = await api.listGradebookColumns();

                // Association
                try {
                    const assoc = await backend.getAssociationForCourse(courseId);
                    setIsAllowed(true);

                    if (assoc) {
                        if (Array.isArray(assoc.subRules) && assoc.subRules.length) {
                            const cleaned = assoc.subRules.map(sanitizeSubRule);
                            setSubRules(cleaned);
                            localStorage.setItem(STORAGE_KEYS.rules, JSON.stringify(cleaned));
                        }
                        if (assoc.logic === "OR" || assoc.logic === "AND") {
                            setLogic(assoc.logic);
                            localStorage.setItem(STORAGE_KEYS.logic, assoc.logic);
                        }
                        if (assoc.performanceScoreId) {
                            setPerformanceScoreId(assoc.performanceScoreId);
                            associationPerfIdRef.current = assoc.performanceScoreId;
                        } else {
                            setPerformanceScoreId(null);
                            associationPerfIdRef.current = null;
                        }

                        const map = assoc.performanceScoreIds && typeof assoc.performanceScoreIds === "object" ? assoc.performanceScoreIds : {};
                        setPerformanceScoreIds(map);
                        perfIdsRef.current = map;
                    }
                } catch (e) {
                    if (e?.is404) setIsAllowed(false);
                    else {
                        console.warn("Association fetch failed:", e?.message || e);
                        setIsAllowed(true);
                    }
                }

                setContents(all.sort((a, b) => a.title.localeCompare(b.title)));
                setStudents(studentObjs.sort((a, b) => a.name.localeCompare(b.name)));
                setColumns(cols);
                setLoading(false);
            } catch (err) {
                setError(err?.response?.data?.message || err.message);
                setLoading(false);
            }
        })();
    }, [api, backend, token, courseId, userUuid, M.messages.missingAccess, M.messages.missingUuid]);

    // sync refs
    useEffect(() => {
        associationPerfIdRef.current = performanceScoreId || null;
    }, [performanceScoreId]);
    useEffect(() => {
        perfIdsRef.current = performanceScoreIds || {};
    }, [performanceScoreIds]);

    // persistence
    useEffect(() => {
        localStorage.setItem(STORAGE_KEYS.mapping, JSON.stringify(mapping));
    }, [mapping]);
    useEffect(() => {
        localStorage.setItem(STORAGE_KEYS.rules, JSON.stringify(subRules));
    }, [subRules]);
    useEffect(() => {
        localStorage.setItem(STORAGE_KEYS.logic, logic);
    }, [logic]);
    useEffect(() => writeBool(STORAGE_KEYS.openRules, openRules), [openRules]);
    useEffect(() => writeBool(STORAGE_KEYS.openMapping, openMapping), [openMapping]);
    useEffect(() => writeBool(STORAGE_KEYS.openEval, openEval), [openEval]);
    useEffect(() => writeBool(STORAGE_KEYS.showStudentPreview, showStudentPreview), [showStudentPreview]);

    /** -------- evaluation -------- */
    const [evaluating, setEvaluating] = useState(false);
    const [rows, setRows] = useState([]);
    const [evSortKey, setEvSortKey] = useState("name"); // name | original | trigger | adjusted
    const [evSortDir, setEvSortDir] = useState("asc");
    const toggleEvSort = (key) => {
        setEvSortKey((prev) => {
            if (prev === key) {
                setEvSortDir((d) => (d === "asc" ? "desc" : "asc"));
                return prev;
            }
            setEvSortDir("asc");
            return key;
        });
    };
    const evAriaSort = (key) => (evSortKey === key ? (evSortDir === "asc" ? "ascending" : "descending") : "none");
    const evGlyph = (key) => sortIcon(evSortKey === key, evSortDir);

    const sortedRows = useMemo(() => {
        const dir = evSortDir === "asc" ? 1 : -1;
        const list = rows.slice();
        list.sort((a, b) => {
            let A, B;
            if (evSortKey === "name") {
                A = (a.name || "").toLowerCase();
                B = (b.name || "").toLowerCase();
            } else if (evSortKey === "original") {
                A = a.originalSchema == null ? Number.POSITIVE_INFINITY : Number(a.originalSchema);
                B = b.originalSchema == null ? Number.POSITIVE_INFINITY : Number(b.originalSchema);
            } else if (evSortKey === "trigger") {
                A = a.rulePass ? 1 : 0;
                B = b.rulePass ? 1 : 0;
            } else {
                A = a.adjustedSchema == null ? Number.POSITIVE_INFINITY : Number(a.adjustedSchema);
                B = b.adjustedSchema == null ? Number.POSITIVE_INFINITY : Number(b.adjustedSchema);
            }
            if (A < B) return -1 * dir;
            if (A > B) return 1 * dir;
            return 0;
        });
        return list;
    }, [rows, evSortKey, evSortDir]);

    async function evaluateAll() {
        setEvaluating(true);
        setInlineError("");
        setInlineSuccess("");
        try {
            const subRuleResults = await Promise.all(
                subRules.map(async (r) => {
                    const content = contents.find((c) => c.id === r.contentId);
                    if (!content) return { id: r.id, byUser: {} };

                    let columnId = content.gradeColumnId;
                    if (!columnId) {
                        const match = columns.find(
                            (c) => (c.name || "").trim().toLowerCase() === (content.title || "").trim().toLowerCase()
                        );
                        if (match) columnId = match.id;
                    }
                    const byUser = {};
                    for (const s of students) {
                        let attempts = [];
                        let hasAttempt = false;

                        if (columnId) {
                            try {
                                attempts = await api.listAttemptsForColumn(columnId, s.userId);
                            } catch {
                                attempts = [];
                            }
                            hasAttempt = attempts && attempts.length > 0;
                        }

                        if (r.type === RULE_TYPES.NOT_SUBMITTED) {
                            byUser[s.userId] = !hasAttempt;
                        } else if (r.type === RULE_TYPES.SCORE_BELOW_PERCENT) {
                            let pctFromOverride = null;
                            if (columnId) {
                                try {
                                    const g = await api.getColumnUserGradeV2(columnId, s.userId);
                                    const isOverridden = g && (Boolean(g.overridden) || typeof g.overridden === "string");
                                    const dgScore = g?.displayGrade?.score;
                                    const dgPossible = g?.displayGrade?.possible;
                                    const rawScore = dgScore != null ? dgScore : g?.score;
                                    const colPossible = columns.find((c) => c.id === columnId)?.score?.possible ?? null;
                                    const possible = dgPossible != null ? dgPossible : colPossible;

                                    if (isOverridden && rawScore != null && possible != null && possible > 0) {
                                        pctFromOverride = (rawScore / possible) * 100;
                                    } else if (isOverridden && rawScore != null && possible == null) {
                                        pctFromOverride = rawScore;
                                    }
                                } catch {
                                    /* ignore */
                                }
                            }

                            let latestScorePct = null;
                            if (pctFromOverride != null) {
                                latestScorePct = pctFromOverride;
                            } else if (hasAttempt) {
                                const latest = attempts
                                    .slice()
                                    .sort(
                                        (a, b) =>
                                            new Date(b.attemptDate || b.created || 0) - new Date(a.attemptDate || a.created || 0)
                                    )[0];
                                const possible = columns.find((c) => c.id === columnId)?.score?.possible ?? 100;
                                const raw = latest?.score?.score ?? latest?.score;
                                latestScorePct = raw != null && possible > 0 ? (raw / possible) * 100 : null;
                            }

                            byUser[s.userId] = latestScorePct == null ? true : latestScorePct < (r.thresholdPercent ?? 50);
                        } else {
                            byUser[s.userId] = false;
                        }
                    }
                    return { id: r.id, byUser };
                })
            );

            const combine = (uid) =>
                subRuleResults.length === 0
                    ? false
                    : logic === "AND"
                        ? subRuleResults.every((sr) => !!sr.byUser[uid])
                        : subRuleResults.some((sr) => !!sr.byUser[uid]);

            const infoByUser = {};
            for (const s of students) {
                const info = (s.userName ? await api.getFinalGradeInfoByUserName(s.userName) : null) ??
                    (await api.getFinalGradeInfoByUserId(s.userId));
                infoByUser[s.userId] = info;
            }

            const out = students.map((s) => {
                const info = infoByUser[s.userId] || { schema: null, columnId: null, overridden: false };
                const originalSchema = info.schema;
                const finalGradeColumnId = info.columnId;
                const overridden = !!info.overridden;
                const rulePass = combine(s.userId);
                const adjustedSchema =
                    originalSchema == null
                        ? null
                        : overridden
                            ? originalSchema
                            : rulePass
                                ? applyMapping(originalSchema, mapping)
                                : originalSchema;

                return {
                    userId: s.userId,
                    userName: s.userName,
                    name: s.name,
                    originalSchema,
                    finalGradeColumnId,
                    overridden,
                    rulePass,
                    adjustedSchema,
                };
            });

            setRows(out);
            setInlineSuccess(M.messages.evalDone(out.length));
        } catch (err) {
            setInlineError(err?.response?.data?.message || err.message || M.messages.evalFailed);
        } finally {
            setEvaluating(false);
        }
    }

    /** -------- writeback -------- */
    const [writing, setWriting] = useState(false);
    async function applyAdjustedGrades() {
        setWriting(true);
        setInlineError("");
        setInlineSuccess("");
        try {
            const notations = await api.listGradeNotations().catch(() => []);
            const notationBySchema = {};
            for (const n of notations) {
                const code = (n?.code || "").trim();
                const m = /^([1-7])xx$/i.exec(code);
                if (m) notationBySchema[m[1]] = n.id;
            }

            let posted = 0,
                skippedOverridden = 0,
                skippedNoNotation = 0,
                skippedNoColumn = 0,
                skippedNoAdj = 0;

            let lastGradeRecordId = null;
            const newPerfIds = { ...perfIdsRef.current };
            const updatedRows = [...rows];

            for (let i = 0; i < rows.length; i++) {
                const r = rows[i];
                if (r.overridden) {
                    skippedOverridden++;
                    continue;
                }
                if (r.adjustedSchema == null) {
                    skippedNoAdj++;
                    continue;
                }

                const schemaStr = String(r.adjustedSchema);
                const perfCodeId = notationBySchema[schemaStr];
                const columnPk1 = r.finalGradeColumnId;
                if (!perfCodeId) {
                    skippedNoNotation++;
                    continue;
                }
                if (!columnPk1) {
                    skippedNoColumn++;
                    continue;
                }

                const writeRes = await api.writePerfCodeToSpecificColumn(columnPk1, r.userId, perfCodeId, false);
                const gradeId = writeRes?.id || writeRes?.gradeId || null;
                if (gradeId) {
                    lastGradeRecordId = gradeId;
                    newPerfIds[r.userId] = gradeId;
                }
                posted++;

                const info = r.userName
                    ? await api.getFinalGradeInfoByUserName(r.userName)
                    : await api.getFinalGradeInfoByUserId(r.userId);

                updatedRows[i] = {
                    ...r,
                    originalSchema: info.schema,
                    finalGradeColumnId: info.columnId || r.finalGradeColumnId,
                    overridden: !!info.overridden,
                    adjustedSchema: info.schema,
                };
            }

            setRows(updatedRows);

            try {
                if (Object.keys(newPerfIds).length) {
                    await backend.savePerformanceScoreIds(courseId, newPerfIds);
                    setPerformanceScoreIds(newPerfIds);
                    perfIdsRef.current = newPerfIds;
                }
                if (lastGradeRecordId) {
                    await backend.savePerformanceScoreId(courseId, lastGradeRecordId);
                    setPerformanceScoreId(lastGradeRecordId);
                    associationPerfIdRef.current = lastGradeRecordId;
                }
            } catch (e) {
                console.warn("Failed to save PerformanceScoreIds:", e?.message || e);
            }

            setInlineSuccess(M.messages.writeDone(posted, skippedOverridden, skippedNoAdj, skippedNoNotation, skippedNoColumn));
        } catch (err) {
            setInlineError(err?.response?.data?.message || err.message || M.messages.writeFailed);
        } finally {
            setWriting(false);
        }
    }

    /** -------- clear override (single) -------- */
    async function clearOverrideFor(row) {
        setInlineError("");
        setInlineSuccess("");
        setClearing((prev) => ({ ...prev, [row.userId]: true }));
        try {
            if (!row.finalGradeColumnId) throw new Error("Missing finalGrade columnId for this user.");
            const colId = row.finalGradeColumnId;

            let gradeIdToClear = perfIdsRef.current?.[row.userId] || null;
            try {
                const records = await api.listGradesForColumnAndUser(colId, row.userId);
                if (gradeIdToClear && !records.some((r) => r?.id === gradeIdToClear)) gradeIdToClear = null;
                if (!gradeIdToClear) {
                    const sorted = records
                        .slice()
                        .sort((a, b) => new Date(b?.modified || b?.created || 0) - new Date(a?.modified || a?.created || 0));
                    gradeIdToClear = sorted.find((r) => r?.perfCodeId || r?.text != null)?.id || sorted[0]?.id || null;
                }
            } catch {
                /* noop */
            }

            if (gradeIdToClear) {
                await api.clearByGradeId(colId, gradeIdToClear, { clearCommentsIfRevertOverrides: false, includeUnpostedGrade: true });
                if (perfIdsRef.current?.[row.userId]) {
                    const copy = { ...perfIdsRef.current };
                    delete copy[row.userId];
                    perfIdsRef.current = copy;
                    setPerformanceScoreIds(copy);
                    try {
                        await backend.savePerformanceScoreIds(courseId, copy);
                    } catch {}
                }
            } else {
                await api.clearOverrideOnSpecificColumn(colId, row.userId);
            }

            const info = row.userName ? await api.getFinalGradeInfoByUserName(row.userName) : await api.getFinalGradeInfoByUserId(row.userId);
            const updatedOriginal = info.schema;
            const updatedOverridden = !!info.overridden;
            const updatedColumnId = info.columnId || colId;
            const newAdjusted =
                updatedOriginal == null
                    ? null
                    : updatedOverridden
                        ? updatedOriginal
                        : row.rulePass
                            ? applyMapping(updatedOriginal, mapping)
                            : updatedOriginal;

            setRows((prev) =>
                prev.map((r) =>
                    r.userId === row.userId
                        ? {
                            ...r,
                            originalSchema: updatedOriginal,
                            finalGradeColumnId: updatedColumnId,
                            overridden: updatedOverridden,
                            adjustedSchema: newAdjusted,
                        }
                        : r
                )
            );

            setInlineSuccess(M.messages.clearedOne(row.name));
        } catch (err) {
            setInlineError(M.messages.clearFailed(row.name, err?.response?.data?.message || err.message));
        } finally {
            setClearing((prev) => {
                const copy = { ...prev };
                delete copy[row.userId];
                return copy;
            });
        }
    }

    /** -------- save rules to association -------- */
    async function saveRulesToAssociation() {
        setRulesMsgError("");
        setRulesMsgSuccess("");
        setSavingAssoc(true);
        try {
            if (!backEndUrl) throw new Error(M.messages.missingBackEnd);
            if (!courseId) throw new Error(M.messages.missingCourse);
            await backend.saveRulesToAssociation(courseId, subRules, logic);
            setRulesMsgSuccess(M.messages.rulesSaved);
        } catch (err) {
            setRulesMsgError(err?.response?.data?.message || err.message || M.messages.rulesFailed);
        } finally {
            setSavingAssoc(false);
        }
    }

    /** -------- student-facing summary (student) -------- */
    function StudentRuleSummaryCard() {
        const titleById = new Map(contents.map((c) => [c.id, c.title]));
        const friendly = (r) => {
            const name = titleById.get(r.contentId) || `Item ${r.contentId}`;
            if (r.type === RULE_TYPES.NOT_SUBMITTED) return M.studentFriendly.notSubmitted(name);
            if (r.type === RULE_TYPES.SCORE_BELOW_PERCENT) return M.studentFriendly.scoreBelow(name, r.thresholdPercent);
            return M.studentFriendly.conditionOn(name);
        };
        const hasRules = Array.isArray(subRules) && subRules.length > 0;

        return (
            <div className="bb-container">
                <style dangerouslySetInnerHTML={{ __html: compactCss }} />
                <div className="bb-card" style={{ padding: 12, border: "1px solid #ddd", borderRadius: 8 }}>
                    <h2 style={{ margin: 0 }}>
                        <b>{M.ui.studentCardTitle}</b>
                    </h2>

                    {!isAllowed ? (
                        <p style={{ fontSize: 12, marginTop: 8 }}>{M.ui.notEnabledStudent}</p>
                    ) : hasRules ? (
                        <>
                            <p style={{ fontSize: 12, marginTop: 8, lineHeight: 1.4 }}>
                                {logic === "AND" ? M.ui.studentCardIntro_all : M.ui.studentCardIntro_any}
                            </p>
                            <ul style={{ fontSize: 12, marginTop: 6 }}>{subRules.map((r) => <li key={r.id}>{friendly(r)}</li>)}</ul>
                            <p style={{ fontSize: 12, marginTop: 8, color: "#555" }}>{M.ui.studentCardFootnote}</p>
                        </>
                    ) : (
                        <p style={{ fontSize: 12, marginTop: 8 }}>{M.ui.noRulesStudent}</p>
                    )}
                </div>
            </div>
        );
    }

    /** -------- instructor preview -------- */
    function StudentRuleSummaryPreview() {
        const titleById = new Map(contents.map((c) => [c.id, c.title]));
        const friendly = (r) => {
            const name = titleById.get(r.contentId) || `Item ${r.contentId}`;
            if (r.type === RULE_TYPES.NOT_SUBMITTED) return M.studentFriendly.notSubmitted(name);
            if (r.type === RULE_TYPES.SCORE_BELOW_PERCENT) return M.studentFriendly.scoreBelow(name, r.thresholdPercent);
            return M.studentFriendly.conditionOn(name);
        };
        const hasRules = Array.isArray(subRules) && subRules.length > 0;

        return (
            <div className="bb-card" style={{ padding: 10, border: "1px dashed #a7f3d0", background: "#ecfdf5", borderRadius: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span role="img" aria-label="eye">👁️</span>
                    <b>{M.ui.previewTitle}</b>
                </div>
                {!isAllowed ? (
                    <p style={{ fontSize: 12, marginTop: 6 }}>{M.ui.notEnabledStudent}</p>
                ) : hasRules ? (
                    <>
                        <p style={{ fontSize: 12, marginTop: 6, lineHeight: 1.4 }}>
                            {logic === "AND" ? M.ui.studentCardIntro_all : M.ui.studentCardIntro_any}
                        </p>
                        <ul style={{ fontSize: 12, marginTop: 4 }}>{subRules.map((r) => <li key={r.id}>{friendly(r)}</li>)}</ul>
                    </>
                ) : (
                    <p style={{ fontSize: 12, marginTop: 6 }}>{M.ui.noRulesStudent}</p>
                )}
            </div>
        );
    }

    const Flash = ({ type, children }) => {
        if (!children) return null;
        const styles = {
            marginBottom: 6,
            padding: "6px 8px",
            borderRadius: 6,
            border: "1px solid",
            background: type === "error" ? "#ffecec" : "#ecfff1",
            borderColor: type === "error" ? "#ffb3b3" : "#b3ffcc",
            color: type === "error" ? "#a40000" : "#005c2e",
            fontSize: 12,
        };
        return (
            <div role={type === "error" ? "alert" : "status"} style={styles} aria-live="polite">
                {children}
            </div>
        );
    };

    /** -------- render gates -------- */
    if (loading) return <div>{M.ui.loading}</div>;
    if (isStudent) return <StudentRuleSummaryCard />;
    if (roleDenied) {
        return (
            <div className="bb-container">
                <style dangerouslySetInnerHTML={{ __html: compactCss }} />
                <div className="bb-card" style={{ padding: 10, border: "1px solid #ddd", borderRadius: 8 }}>
                    <h2 style={{ margin: 0 }}>
                        <b>{M.ui.accessRestrictedTitle}</b>
                    </h2>
                    <p style={{ fontSize: 12, marginTop: 6 }}>{M.ui.accessRestrictedBody}</p>
                </div>
            </div>
        );
    }
    if (error) {
        return (
            <div className="bb-container bb-error">
                <style dangerouslySetInnerHTML={{ __html: compactCss }} />
                <div className="bb-card" style={{ padding: 10, border: "1px solid #ddd", borderRadius: 8 }}>
                    <h2 style={{ margin: 0 }}>
                        <b>{M.ui.errorTitle}</b>
                    </h2>
                    <p style={{ fontSize: 12 }}>{error}</p>
                </div>
            </div>
        );
    }
    if (!isAllowed) {
        return (
            <div className="bb-container">
                <style dangerouslySetInnerHTML={{ __html: compactCss }} />
                <div className="bb-card" style={{ padding: 10, border: "1px solid #ddd", borderRadius: 8 }}>
                    <h2 style={{ margin: 0 }}>
                        <b>{M.ui.capabilityOffTitle}</b>
                    </h2>
                    <p style={{ fontSize: 12, marginTop: 6 }}>{M.ui.capabilityOffBody}</p>
                </div>
            </div>
        );
    }

    /** -------- main -------- */
    return (
        <div className="bb-container" style={{ display: "grid", gap: 12 }}>
            <style dangerouslySetInnerHTML={{ __html: compactCss }} />

            {/* Rule Builder */}
            <Section
                title={
                    <span>
            <b>{M.ui.ruleBuilderTitle}</b>
          </span>
                }
                open={openRules}
                onToggle={() => setOpenRules((o) => !o)}
                right={
                    <button
                        type="button"
                        className="bb-button"
                        onClick={saveRulesToAssociation}
                        disabled={savingAssoc}
                        title="Save Sub-Rules & Logic to the course association"
                        aria-busy={savingAssoc}
                    >
                        {savingAssoc ? M.ui.savingRules : M.ui.saveRules}
                    </button>
                }
            >
                <Flash type="error">{rulesMsgError}</Flash>
                <Flash type="success">{rulesMsgSuccess}</Flash>

                <div style={{ display: "grid", gap: 8 }}>
                    {subRules.map((r, idx) => (
                        <div
                            key={r.id}
                            style={{
                                display: "grid",
                                gap: 6,
                                gridTemplateColumns: "0.8fr 2fr 1fr 120px",
                                alignItems: "center",
                            }}
                        >
                            <span>{M.ui.ruleLabel(idx + 1)}</span>

                            <select value={r.contentId} onChange={(e) => setSubRules((prev) => prev.map((x) => (x.id === r.id ? { ...x, contentId: e.target.value } : x)))}>
                                <option value="">{M.ui.selectContent}</option>
                                {contents.map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {c.title}
                                    </option>
                                ))}
                            </select>

                            <select value={r.type} onChange={(e) => setSubRules((prev) => prev.map((x) => (x.id === r.id ? { ...x, type: e.target.value } : x)))}>
                                <option value={RULE_TYPES.NOT_SUBMITTED}>{M.ui.ruleType_notSubmitted}</option>
                                <option value={RULE_TYPES.SCORE_BELOW_PERCENT}>{M.ui.ruleType_belowPct}</option>
                            </select>

                            {r.type === RULE_TYPES.SCORE_BELOW_PERCENT ? (
                                <input
                                    type="number"
                                    min={0}
                                    max={100}
                                    value={r.thresholdPercent}
                                    onChange={(e) =>
                                        setSubRules((prev) =>
                                            prev.map((x) =>
                                                x.id === r.id ? { ...x, thresholdPercent: clamp(parseNumber(e.target.value, 50), 0, 100) } : x
                                            )
                                        )
                                    }
                                    placeholder={M.ui.thresholdPlaceholder}
                                />
                            ) : (
                                <button type="button" className="bb-button danger" onClick={() => setSubRules((prev) => prev.filter((x) => x.id !== r.id))}>
                                    {M.ui.remove}
                                </button>
                            )}
                        </div>
                    ))}

                    <div>
                        <button type="button" className="bb-button" onClick={() => setSubRules((prev) => [...prev, makeEmptySubRule()])}>
                            {M.ui.addedSubRule}
                        </button>
                    </div>
                    <div>
                        {M.ui.combine}&nbsp;
                        <select value={logic} onChange={(e) => setLogic(e.target.value)}>
                            <option value="AND">{M.ui.and}</option>
                            <option value="OR">{M.ui.or}</option>
                        </select>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <button
                            type="button"
                            className="bb-button"
                            onClick={() => setShowStudentPreview((v) => !v)}
                            aria-pressed={showStudentPreview}
                            title={showStudentPreview ? M.ui.hidePreview : M.ui.showPreview}
                        >
                            {showStudentPreview ? M.ui.hidePreview : M.ui.showPreview}
                        </button>
                    </div>
                    {showStudentPreview && <StudentRuleSummaryPreview />}
                </div>
            </Section>

            {/* Mapping */}
            <Section title={<b>{M.ui.mappingTitle}</b>} open={openMapping} onToggle={() => setOpenMapping((o) => !o)}>
                <table className="bb-table">
                    <thead>
                    <tr>
                        <th>From Min (schema)</th>
                        <th>From Max (schema)</th>
                        <th>To (schema)</th>
                        <th></th>
                    </tr>
                    </thead>
                    <tbody>
                    {mapping.map((m, i) => (
                        <tr key={`${m.fromMin}-${m.fromMax}-${i}`}>
                            <td>
                                <input
                                    type="number"
                                    value={m.fromMin}
                                    onChange={(e) => {
                                        const v = clamp(parseNumber(e.target.value, m.fromMin), 1, 7);
                                        const copy = [...mapping];
                                        copy[i] = { ...m, fromMin: v };
                                        setMapping(copy);
                                    }}
                                />
                            </td>
                            <td>
                                <input
                                    type="number"
                                    value={m.fromMax}
                                    onChange={(e) => {
                                        const v = clamp(parseNumber(e.target.value, m.fromMax), 1, 7);
                                        const copy = [...mapping];
                                        copy[i] = { ...m, fromMax: v };
                                        setMapping(copy);
                                    }}
                                />
                            </td>
                            <td>
                                <input
                                    type="number"
                                    value={m.to}
                                    onChange={(e) => {
                                        const v = clamp(parseNumber(e.target.value, m.to), 1, 7);
                                        const copy = [...mapping];
                                        copy[i] = { ...m, to: v };
                                        setMapping(copy);
                                    }}
                                />
                            </td>
                            <td>
                                <button type="button" className="bb-button danger" onClick={() => setMapping((prev) => prev.filter((_, idx) => idx !== i))}>
                                    {M.ui.remove}
                                </button>
                            </td>
                        </tr>
                    ))}
                    <tr>
                        <td colSpan={4}>
                            <button type="button" className="bb-button" onClick={() => setMapping((prev) => [...prev, { fromMin: 1, fromMax: 1, to: 1 }])}>
                                {M.ui.addMappingRow}
                            </button>
                        </td>
                    </tr>
                    </tbody>
                </table>
                <p style={{ fontStyle: "italic", marginTop: 6, fontSize: 12 }}>{M.ui.mappingExample}</p>
            </Section>

            {/* Evaluate */}
            <Section title={<b>{M.ui.evaluateTitle}</b>} open={openEval} onToggle={() => setOpenEval((o) => !o)}>
                <Flash type="error">{inlineError}</Flash>
                <Flash type="success">{inlineSuccess}</Flash>

                <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                    <button className="bb-button" onClick={evaluateAll} disabled={evaluating} aria-busy={evaluating}>
                        {evaluating ? M.ui.evaluating : M.ui.evaluate}
                    </button>
                    <button className="bb-button" onClick={applyAdjustedGrades} disabled={writing || rows.length === 0} aria-busy={writing}>
                        {writing ? M.ui.applying : M.ui.apply}
                    </button>
                </div>

                <table className="bb-table" role="grid">
                    <thead>
                    <tr>
                        <th
                            role="button"
                            tabIndex={0}
                            aria-sort={evAriaSort("name")}
                            onClick={() => toggleEvSort("name")}
                            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && toggleEvSort("name")}
                            title={M.ui.sortByStudent}
                            style={{ cursor: "pointer", userSelect: "none" }}
                        >
                            {M.ui.studentCol} <span style={{ marginLeft: 6, opacity: 0.7 }}>{evGlyph("name")}</span>
                        </th>
                        <th
                            role="button"
                            tabIndex={0}
                            aria-sort={evAriaSort("original")}
                            onClick={() => toggleEvSort("original")}
                            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && toggleEvSort("original")}
                            title={M.ui.sortByCurrent}
                            style={{ cursor: "pointer", userSelect: "none" }}
                        >
                            {M.ui.currentSchemaCol} <span style={{ marginLeft: 6, opacity: 0.7 }}>{evGlyph("original")}</span>
                        </th>
                        <th
                            role="button"
                            tabIndex={0}
                            aria-sort={evAriaSort("trigger")}
                            onClick={() => toggleEvSort("trigger")}
                            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && toggleEvSort("trigger")}
                            title={M.ui.sortByTrigger}
                            style={{ cursor: "pointer", userSelect: "none" }}
                        >
                            {M.ui.triggersCol} <span style={{ marginLeft: 6, opacity: 0.7 }}>{evGlyph("trigger")}</span>
                        </th>
                        <th
                            role="button"
                            tabIndex={0}
                            aria-sort={evAriaSort("adjusted")}
                            onClick={() => toggleEvSort("adjusted")}
                            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && toggleEvSort("adjusted")}
                            title={M.ui.sortByAdjusted}
                            style={{ cursor: "pointer", userSelect: "none" }}
                        >
                            {M.ui.adjustedCol} <span style={{ marginLeft: 6, opacity: 0.7 }}>{evGlyph("adjusted")}</span>
                        </th>
                        <th>{M.ui.actionsCol}</th>
                    </tr>
                    </thead>
                    <tbody>
                    {sortedRows.length === 0 ? (
                        <tr>
                            <td colSpan={5} style={{ textAlign: "center" }}>
                                {M.ui.noEvalRows}
                            </td>
                        </tr>
                    ) : (
                        sortedRows.map((r) => (
                            <tr key={r.userId}>
                                <td>{r.name}</td>
                                <td>
                                    {r.originalSchema ?? "—"}
                                    {r.overridden ? M.ui.overriddenSuffix : ""}
                                </td>
                                <td>{r.rulePass ? M.ui.yes : M.ui.no}</td>
                                <td>{r.adjustedSchema ?? "—"}</td>
                                <td>
                                    <button
                                        className="bb-button"
                                        onClick={() => clearOverrideFor(r)}
                                        disabled={!r.overridden || !!clearing[r.userId]}
                                        title={r.overridden ? M.ui.clearOverride : ""}
                                        aria-disabled={!r.overridden || !!clearing[r.userId]}
                                    >
                                        {clearing[r.userId] ? M.ui.clearing : M.ui.clearOverride}
                                    </button>
                                </td>
                            </tr>
                        ))
                    )}
                    </tbody>
                </table>
            </Section>
        </div>
    );
};

export default GradeHurdleCourseDetails;
